/**
 * Account popover with profile, settings, and logout.
 */
import React from "react";
import { Icon } from "@iconify/react";
import { Box, Divider } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import VAvatar from "form/VAvatar";
import VButton from "form/VButton";
import VText from "form/VText";
import { useAsterController } from "context";
import { clearSyncQueue } from "offline/queue";
import { getUserName } from "utils/string";

/**
 * User menu that navigates to profile/settings and clears JWTs on logout.
 *
 * @param {{ handleClose: Function }} props
 */
const HeaderMenu = ({ handleClose }) => {
  const { t } = useTranslation("common");

  const navigate = useNavigate();

  const [controller] = useAsterController();
  const { user } = controller;

  /**
   * Closes the menu and navigates to the given path.
   *
   * @param {string} url Destination path.
   * @returns {void}
   */
  const onClick = (url) => {
    handleClose();
    navigate(url);
  };

  /**
   * Clears both JWT keys before the logout redirect.
   *
   * @returns {void}
   */
  const onLogoutClick = () => {
    void clearSyncQueue();
    try {
      localStorage.removeItem("jwt");
      localStorage.removeItem("jwt-ghost");
    } catch (e) {
      // ignore storage errors during logout
    }
  };

  const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");
  const logoutHref = `${base}/logout?redirect_url=${encodeURIComponent(`${base}/auth/login`)}`;

  return (
    <Box className="w-64 my-2">
      <VButton variant="text" className="flex items-center w-full justify-start px-4 normal-case" onClick={onClick}>
        <VAvatar size="sm" bgColor="light" src={user.avatar} />
        <Box className="text-left ml-2">
          <VText className="text-lg font-bold leading-5 text-limit-1">{getUserName(user) || "-"}</VText>
          <VText className="text-sm text-gray-400 leading-4 text-limit-1" color="primary">
            {user.permission || "-"}
          </VText>
        </Box>
      </VButton>
      <Divider className="my-4 bg-gray-200 dark:v-bg-body" />
      <Box className="mx-1.5">
        <VButton variant="text" color="body" className="flex items-center w-full justify-start px-4 text-left normal-case" onClick={() => onClick("/profile")}>
          <VText className="text-base leading-5 text-limit-1 flex items-center" color="custom">
            <Icon icon="ci:user-circle" className="text-xl" />
            &nbsp;&nbsp;{t("Profile")}
          </VText>
        </VButton>

        <VButton variant="text" color="body" className="flex items-center w-full justify-start px-4 text-left normal-case" onClick={() => onClick("/settings")}>
          <VText className="text-base leading-5 text-limit-1 flex items-center" color="custom">
            <Icon icon="uil:setting" className="text-xl" />
            &nbsp;&nbsp;{t("Settings")}
          </VText>
        </VButton>
      </Box>
      <Divider className="my-4 bg-gray-200 dark:v-bg-body" />
      <Box className="px-4">
        <a href={logoutHref} rel="noopener noreferrer" className="no-underline">
          <VButton variant="contained" color="primary" className="w-full normal-case" onClick={onLogoutClick}>
            <Icon icon="ic:outline-logout" className="text-xl" />
            &nbsp;&nbsp;{t("Logout")}
          </VButton>
        </a>
      </Box>
    </Box>
  );
};

export default HeaderMenu;
